'use client';

/**
 * @module
 * @category Component
 */

import 'BomdisoftComponents/inputs/phone/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { Select } from 'BomdisoftComponents/inputs/field/select/index';
import { usePhoneInputStore } from 'BomdisoftComponents/inputs/phone/store';
import { countryCode, getLanguageFromLocale, phoneT } from '@bomdisoft/translations';
import { COUNTRY_DETAILS } from '@bomdisoft/utilities';
import { useParams } from 'next/navigation';
import { type ChangeEvent, type KeyboardEvent, useId } from 'react';

type PhoneInputProps = {
  label?: string;
  helperText?: string;
  required?: boolean;
  onKeyUp?: (event: KeyboardEvent) => void;
  className?: string;
};

export const PhoneInput = ({ label, helperText, required, onKeyUp = () => null, className }: PhoneInputProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const id = useId();
  const extension = usePhoneInputStore(store => store.extension);
  const number = usePhoneInputStore(store => store.number);
  const setPhoneExtension = usePhoneInputStore(store => store.setPhoneExtension);
  const setPhoneNumber = usePhoneInputStore(store => store.setPhoneNumber);

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => setPhoneNumber(Number(event.target.value));
  const handleExtensionChange = (event: ChangeEvent<HTMLSelectElement>) => setPhoneExtension(event.target.value);

  return (
    <div className={joinClasses('bomdisoft-phone-input', className)}>
      <fieldset aria-labelledby={`${id}-label`} className='bomdisoft-phone-input-group'>
        <label className='bomdisoft-phone-input-label' data-required={required ? '' : undefined} htmlFor={`${id}-number`} id={`${id}-label`}>
          {label || phoneT[language]}
        </label>
        <Select aria-label={countryCode[language]} className='bomdisoft-phone-input-extension-wrap' onChange={handleExtensionChange} size='small' value={extension} variant='bare'>
          {COUNTRY_DETAILS.map(option => (
            <option key={option.name} value={option.id}>
              {option.flag} {option.id}
            </option>
          ))}
        </Select>
        <input aria-describedby={helperText ? `${id}-helper` : undefined} className='bomdisoft-phone-input-number' id={`${id}-number`} inputMode='numeric' onChange={handlePhoneChange} onKeyUp={onKeyUp} pattern='[0-9]*' required={required} type='tel' value={number || ''} />
      </fieldset>
      {helperText && (
        <p className='bomdisoft-phone-input-helper' id={`${id}-helper`}>
          {helperText}
        </p>
      )}
    </div>
  );
};
